import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapContainer, GeoJSON, Polygon, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Sparkles, MapPin, Plus, Trash2, Eye, EyeOff } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ZONE_TYPE_LABEL = {
    regione: "Regione",
    provincia: "Provincia",
    denominazione: "Denominazione",
};

export default function AdminMapZones() {
    const [zones, setZones] = useState([]);
    const [unmapped, setUnmapped] = useState({ regioni_unmapped: [], denominazioni_unmapped: [] });
    const [filterType, setFilterType] = useState("");
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // { name, country, region, zone_type, parent_region }
    const [proposal, setProposal] = useState(null);
    const [proposing, setProposing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [customAliases, setCustomAliases] = useState("");

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get("/admin/map/zones"),
            api.get("/admin/map/unmapped"),
        ])
            .then(([zRes, uRes]) => {
                setZones(zRes.data.items || []);
                setUnmapped(uRes.data || { regioni_unmapped: [], denominazioni_unmapped: [] });
            })
            .catch(() => toast.error("Errore caricamento zone"))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const openCreate = (init) => {
        setModal(init);
        setProposal(null);
        setCustomAliases("");
    };

    const proposeZone = async () => {
        if (!modal) return;
        setProposing(true);
        setProposal(null);
        try {
            const { data } = await api.post("/admin/map/zones/propose", {
                name: modal.name,
                country: modal.country,
                region: modal.region,
                zone_type: modal.zone_type,
                parent_region: modal.parent_region || null,
            });
            setProposal(data);
            if (!data.polygon) toast.info("L'AI non è sicura — puoi comunque salvarla vuota o riprovare");
        } catch (e) {
            toast.error("Errore proposta AI");
        } finally {
            setProposing(false);
        }
    };

    const saveZone = async () => {
        if (!modal) return;
        setSaving(true);
        const aliases = customAliases.split(",").map((a) => a.trim()).filter(Boolean);
        try {
            await api.post("/admin/map/zones", {
                name: modal.name,
                country: modal.country,
                region: modal.region,
                zone_type: modal.zone_type,
                parent_region: modal.parent_region || null,
                aliases,
                polygon: proposal?.polygon || null,
                center: proposal?.center || null,
                attiva: true,
            });
            toast.success(`Zona ${modal.name} creata`);
            setModal(null);
            load();
        } catch {
            toast.error("Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (z) => {
        try {
            await api.put(`/admin/map/zones/${z.id}`, { attiva: !z.attiva });
            load();
        } catch {
            toast.error("Errore aggiornamento");
        }
    };

    const removeZone = async (z) => {
        if (!confirm(`Eliminare la zona "${z.name}"?`)) return;
        try {
            await api.delete(`/admin/map/zones/${z.id}`);
            toast.success("Zona eliminata");
            load();
        } catch {
            toast.error("Errore eliminazione");
        }
    };

    const filteredZones = filterType ? zones.filter((z) => z.zone_type === filterType) : zones;

    return (
        <div className="co-container py-8" data-testid="admin-map-zones-page">
            <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:justify-between mb-6">
                <div>
                    <span className="co-caption block mb-1">Amministrazione</span>
                    <h1 className="text-3xl sm:text-4xl">Zone mappa</h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl">
                        Crescita incrementale: le zone si aggiungono man mano che entrano vini di
                        nuove aree. La mappa pubblica mostra solo zone con vini disponibili.
                    </p>
                </div>
                <Select value={filterType || "__all"} onValueChange={(v) => setFilterType(v === "__all" ? "" : v)}>
                    <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="admin-map-zones-type-filter">
                        <SelectValue placeholder="Tipologia" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">Tutte</SelectItem>
                        <SelectItem value="regione">Regioni</SelectItem>
                        <SelectItem value="provincia">Province</SelectItem>
                        <SelectItem value="denominazione">Denominazioni</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {loading && <div className="italic co-caption">Caricamento…</div>}

            {/* Unmapped section */}
            {!loading && (unmapped.regioni_unmapped.length > 0 || unmapped.denominazioni_unmapped.length > 0) && (
                <section className="mb-10" data-testid="admin-map-unmapped-section">
                    <h2 className="text-xl mb-3">Zone non mappate</h2>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
                        Regioni e denominazioni con vini in carta che non hanno ancora una zona attiva sulla mappa.
                    </p>
                    {unmapped.regioni_unmapped.length > 0 && (
                        <div className="mb-4">
                            <div className="co-caption mb-2">Regioni ({unmapped.regioni_unmapped.length})</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                {unmapped.regioni_unmapped.slice(0, 30).map((it) => (
                                    <div key={`${it.country}|${it.region}`}
                                        className="flex items-center justify-between bg-white border border-[hsl(var(--border))] px-3 py-2 rounded-sm text-sm"
                                        data-testid={`unmapped-region-${it.region}`}>
                                        <div>
                                            <div className="font-medium">{it.region}</div>
                                            <div className="text-xs text-[hsl(var(--muted-foreground))]">{it.country} · {it.wine_count} vini</div>
                                        </div>
                                        <button className="co-btn-ghost text-xs"
                                            onClick={() => openCreate({
                                                name: it.region, country: it.country, region: it.region,
                                                zone_type: "regione", parent_region: null,
                                            })}
                                            data-testid={`unmapped-region-create-${it.region}`}>
                                            <Plus className="w-3 h-3" /> Crea
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {unmapped.denominazioni_unmapped.length > 0 && (
                        <div>
                            <div className="co-caption mb-2">Denominazioni ({unmapped.denominazioni_unmapped.length})</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                {unmapped.denominazioni_unmapped.slice(0, 30).map((it) => (
                                    <div key={`${it.country}|${it.region}|${it.denomination}`}
                                        className="flex items-center justify-between bg-white border border-[hsl(var(--border))] px-3 py-2 rounded-sm text-sm"
                                        data-testid={`unmapped-denom-${it.denomination}`}>
                                        <div>
                                            <div className="font-medium">{it.denomination}</div>
                                            <div className="text-xs text-[hsl(var(--muted-foreground))]">{it.country} · {it.region || "—"} · {it.wine_count} vini</div>
                                        </div>
                                        <button className="co-btn-ghost text-xs"
                                            onClick={() => openCreate({
                                                name: it.denomination, country: it.country,
                                                region: it.denomination, zone_type: "denominazione",
                                                parent_region: it.region,
                                            })}>
                                            <Plus className="w-3 h-3" /> Crea
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </section>
            )}

            {/* Zones list */}
            {!loading && (
                <section data-testid="admin-map-zones-list">
                    <h2 className="text-xl mb-3">Zone in DB ({filteredZones.length})</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-[hsl(var(--border))] text-left">
                                    <th className="py-2 pr-3">Nome</th>
                                    <th className="py-2 pr-3">Paese</th>
                                    <th className="py-2 pr-3">Tipo</th>
                                    <th className="py-2 pr-3">Regione padre</th>
                                    <th className="py-2 pr-3 text-right">Vini</th>
                                    <th className="py-2 pr-3">Source</th>
                                    <th className="py-2 pr-3">Attiva</th>
                                    <th className="py-2 pr-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredZones.slice(0, 200).map((z) => (
                                    <tr key={z.id} className="border-b border-[hsl(var(--border))]/50">
                                        <td className="py-2 pr-3">
                                            <MapPin className="w-3 h-3 inline mr-1 text-[hsl(var(--primary))]" strokeWidth={1.5}/>
                                            {z.name}
                                        </td>
                                        <td className="py-2 pr-3">{z.country}</td>
                                        <td className="py-2 pr-3 text-xs">{ZONE_TYPE_LABEL[z.zone_type]}</td>
                                        <td className="py-2 pr-3 text-xs text-[hsl(var(--muted-foreground))]">{z.parent_region || "—"}</td>
                                        <td className="py-2 pr-3 text-right font-medium">{z.wine_count || 0}</td>
                                        <td className="py-2 pr-3 text-xs text-[hsl(var(--muted-foreground))]">{z.source}</td>
                                        <td className="py-2 pr-3">
                                            <button onClick={() => toggleActive(z)} data-testid={`admin-map-zone-toggle-${z.id}`}>
                                                {z.attiva ? <Eye className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5}/> :
                                                            <EyeOff className="w-4 h-4 text-[hsl(var(--muted-foreground))]" strokeWidth={1.5}/>}
                                            </button>
                                        </td>
                                        <td className="py-2 pr-3">
                                            <button className="text-[hsl(var(--destructive))] hover:opacity-70"
                                                onClick={() => removeZone(z)}
                                                data-testid={`admin-map-zone-delete-${z.id}`}>
                                                <Trash2 className="w-3 h-3" strokeWidth={1.5}/>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* Create dialog */}
            <Dialog open={!!modal} onOpenChange={(v) => !v && setModal(null)}>
                <DialogContent className="max-w-2xl" data-testid="admin-map-zone-create-dialog">
                    <DialogHeader>
                        <DialogTitle>Crea zona: {modal?.name}</DialogTitle>
                    </DialogHeader>
                    {modal && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="co-caption block mb-1">Paese</label>
                                    <Input value={modal.country} onChange={(e) => setModal({ ...modal, country: e.target.value })}/>
                                </div>
                                <div>
                                    <label className="co-caption block mb-1">Tipo</label>
                                    <Select value={modal.zone_type} onValueChange={(v) => setModal({ ...modal, zone_type: v })}>
                                        <SelectTrigger className="bg-white"><SelectValue/></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="regione">Regione</SelectItem>
                                            <SelectItem value="denominazione">Denominazione</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="co-caption block mb-1">Region key (deve matchare wine.region o wine.denomination)</label>
                                    <Input value={modal.region} onChange={(e) => setModal({ ...modal, region: e.target.value })}/>
                                </div>
                                <div>
                                    <label className="co-caption block mb-1">Region padre (opz.)</label>
                                    <Input value={modal.parent_region || ""} onChange={(e) => setModal({ ...modal, parent_region: e.target.value })}/>
                                </div>
                            </div>
                            <div>
                                <label className="co-caption block mb-1">Alias (comma-separated, es. "Borgogna, Burgundy")</label>
                                <Input value={customAliases} onChange={(e) => setCustomAliases(e.target.value)}/>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <button onClick={proposeZone} disabled={proposing} className="co-btn-ghost text-sm"
                                    data-testid="admin-map-zone-propose-btn">
                                    <Sparkles className="w-4 h-4"/>{proposing ? "Elaboro…" : (proposal ? "Rigenera" : "Proponi poligono (AI)")}
                                </button>
                                <button onClick={saveZone} disabled={saving} className="co-btn-primary text-sm"
                                    data-testid="admin-map-zone-save-btn">
                                    {saving ? "Salvo…" : "Salva zona"}
                                </button>
                            </div>
                            {proposal && (
                                <div className="mt-2 p-3 bg-[hsl(var(--secondary))] rounded-sm text-xs">
                                    <div>
                                        <span className="co-caption">AI Confidence: </span>
                                        <span className={proposal.confidence === "high" ? "text-[hsl(var(--primary))]" : ""}>{proposal.confidence}</span>
                                    </div>
                                    {proposal.notes && <div className="mt-1 italic">{proposal.notes}</div>}
                                    {proposal.polygon && proposal.center && (
                                        <div className="mt-3">
                                            <MapContainer center={proposal.center} zoom={7}
                                                style={{ height: 280, width: "100%" }}
                                                scrollWheelZoom={false} data-testid="admin-map-zone-preview">
                                                <TileLayer attribution='&copy; OSM' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                                <Polygon positions={proposal.polygon}
                                                    pathOptions={{ color: "#5C0F1F", fillColor: "#8B0033", fillOpacity: 0.25 }}/>
                                            </MapContainer>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
